import { getLowestStorePrice, getProductKey } from './priceUtils';

export function extractPricedProducts(products = []) {
  return products
    .map((product) => {
      const price = getLowestStorePrice(product);
      const key = getProductKey(product);
      if (!key || !Number.isFinite(price) || price <= 0 || price === Infinity) {
        return null;
      }
      return { key, price, product };
    })
    .filter(Boolean);
}

export function buildHistogramBuckets(pricedProducts, bounds, bucketCount = 18) {
  const min = Number(bounds.min) || 0;
  const max = Math.max(Number(bounds.max) || min + 1, min + 1);
  const span = max - min;
  const bucketSize = span / bucketCount;

  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    index,
    min: min + index * bucketSize,
    max: index === bucketCount - 1 ? max : min + (index + 1) * bucketSize,
    count: 0,
    firstProductKey: null,
    lowestPrice: Infinity,
  }));

  pricedProducts.forEach(({ key, price }) => {
    let bucketIndex = Math.floor((price - min) / bucketSize);
    if (bucketIndex >= bucketCount) {
      bucketIndex = bucketCount - 1;
    }
    if (bucketIndex < 0) {
      bucketIndex = 0;
    }

    const bucket = buckets[bucketIndex];
    bucket.count += 1;
    if (bucket.firstProductKey == null || price < bucket.lowestPrice) {
      bucket.firstProductKey = key;
      bucket.lowestPrice = price;
    }
  });

  const peakCount = Math.max(...buckets.map((bucket) => bucket.count), 1);

  return {
    min,
    max,
    bucketCount,
    buckets: buckets
      .map((bucket) => {
        const midpoint = (bucket.min + bucket.max) / 2;
        const priceHeightRatio = span > 0 ? (midpoint - min) / span : 0;

        return {
          ...bucket,
          midpoint,
          priceHeightRatio,
          densityRatio: bucket.count / peakCount,
        };
      })
      .sort((a, b) => a.min - b.min),
  };
}

export function findFirstProductInBracket(products, min, max) {
  const matches = products
    .map((product) => ({
      product,
      price: getLowestStorePrice(product),
    }))
    .filter(({ price }) => price >= min && price <= max)
    .sort((a, b) => a.price - b.price);

  return matches.length ? matches[0].product : null;
}

export function priceToBucketIndex(price, bounds, bucketCount = 18) {
  const min = Number(bounds.min) || 0;
  const max = Math.max(Number(bounds.max) || min + 1, min + 1);
  const span = max - min;
  const bucketSize = span / bucketCount;

  let index = Math.floor((price - min) / bucketSize);
  if (index >= bucketCount) index = bucketCount - 1;
  if (index < 0) index = 0;
  return index;
}

export function bucketCenterPercent(index, bucketCount) {
  return ((index + 0.5) / bucketCount) * 100;
}

export function getHistogramPriceBounds(products = []) {
  const prices = extractPricedProducts(products)
    .map((entry) => entry.price)
    .filter((price) => Number.isFinite(price) && price > 0);

  if (!prices.length) {
    return { min: 0, max: 1000 };
  }

  return {
    min: Math.floor(Math.min(...prices)),
    max: Math.ceil(Math.max(...prices)),
  };
}

function mixChannel(a, b, ratio) {
  return Math.round(a + (b - a) * ratio);
}

export function getPriceBucketColor(ratio, options = {}) {
  const { hover = false, empty = false } = options;
  const green = [34, 197, 94];
  const blue = [59, 130, 246];
  const red = [239, 68, 68];
  const t = Math.max(0, Math.min(1, ratio));
  let rgb;

  if (t <= 0.5) {
    const local = t * 2;
    rgb = [
      mixChannel(green[0], blue[0], local),
      mixChannel(green[1], blue[1], local),
      mixChannel(green[2], blue[2], local),
    ];
  } else {
    const local = (t - 0.5) * 2;
    rgb = [
      mixChannel(blue[0], red[0], local),
      mixChannel(blue[1], red[1], local),
      mixChannel(blue[2], red[2], local),
    ];
  }

  let alpha = empty ? 0.28 : 0.72;
  if (!empty) {
    alpha += 0.2 * Math.min(1, options.density || 0);
  }
  if (hover) {
    alpha = Math.min(1, alpha + 0.18);
  }

  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha.toFixed(2)})`;
}
